import React, { useState } from 'react';
import axios from 'axios';
import Button from '../UI/Button';
import { useAppSelector } from '../../hooks/redux';
import { addToast } from '../../store/slices/uiSlice';
import { useAppDispatch } from '../../hooks/redux';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

export interface DocumentUploadProps {
  type: 'passport' | 'drivers_license' | 'national_id';
  frontImage?: string;
  backImage?: string;
  onUploadComplete?: (side: 'front' | 'back', url: string) => void;
  disabled?: boolean;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  type,
  frontImage,
  backImage,
  onUploadComplete,
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const { accessToken } = useAppSelector((state) => state.auth);
  const [uploading, setUploading] = useState<{ front: boolean; back: boolean }>({ front: false, back: false });
  const isPassport = type === 'passport';

  const handleFileUpload = async (side: 'front' | 'back', file: File) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      dispatch(addToast({ message: 'Please upload an image file', type: 'error' }));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      dispatch(addToast({ message: 'File size must be less than 5MB', type: 'error' }));
      return;
    }

    setUploading((prev) => ({ ...prev, [side]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      formData.append('side', side);

      const response = await axios.post(
        `${API_BASE_URL}/kyc/documents/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const url = response.data.data.url;
      if (onUploadComplete) {
        onUploadComplete(side, url);
      }

      dispatch(addToast({ 
        message: `${side === 'front' ? 'Front' : 'Back'} image uploaded successfully. Document data will be extracted automatically.`, 
        type: 'success' 
      }));
    } catch (error: any) {
      console.error('Error uploading document:', error);
      dispatch(addToast({
        message: error.response?.data?.error?.message || 'Failed to upload document',
        type: 'error',
      }));
    } finally {
      setUploading((prev) => ({ ...prev, [side]: false }));
    }
  };

  const handleFileSelect = (side: 'front' | 'back', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(side, file);
    }
    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const getDocumentTypeLabel = () => {
    switch (type) {
      case 'passport':
        return 'Passport';
      case 'drivers_license':
        return "Driver's License";
      case 'national_id':
        return 'National ID';
      default:
        return 'Document';
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-[var(--color-info-50)] border border-[var(--color-info-200)] rounded-lg">
        <p className="text-sm text-[var(--color-info-700)]">
          {isPassport 
            ? 'Upload your passport photo. Document number and expiration date will be extracted automatically.'
            : 'Upload both sides of your document. Document number and expiration date will be extracted automatically.'}
        </p>
      </div>

      <div className={`grid gap-4 ${isPassport ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
        {/* Front Image */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--color-default-font)]">
            {getDocumentTypeLabel()} - Front
          </label>
          <div className="border-2 border-dashed border-[var(--color-neutral-border)] rounded-lg p-4">
            {frontImage ? (
              <div className="space-y-2">
                <img
                  src={frontImage.startsWith('http') ? frontImage : `${API_BASE_URL.replace('/api', '')}${frontImage}`}
                  alt="Front"
                  className="w-full h-48 object-contain rounded"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect('front', e)}
                  disabled={disabled || uploading.front}
                  className="hidden"
                  id={`${type}-front-upload`}
                />
                <label htmlFor={`${type}-front-upload`}>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={disabled || uploading.front}
                    isLoading={uploading.front}
                    className="w-full"
                  >
                    {uploading.front ? 'Uploading...' : 'Replace Front Image'}
                  </Button>
                </label>
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect('front', e)}
                  disabled={disabled || uploading.front}
                  className="hidden"
                  id={`${type}-front-upload`}
                />
                <label htmlFor={`${type}-front-upload`}>
                  <div className="flex flex-col items-center justify-center py-8 cursor-pointer hover:bg-[var(--color-neutral-50)] rounded transition-colors">
                    <svg className="w-12 h-12 text-[var(--color-neutral-400)] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-[var(--color-subtext-color)]">Click to upload front image</p>
                    <p className="text-xs text-[var(--color-subtext-color)] mt-1">Max 5MB</p>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Back Image - Not shown for passport */}
        {!isPassport && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--color-default-font)]">
              {getDocumentTypeLabel()} - Back
            </label>
          <div className="border-2 border-dashed border-[var(--color-neutral-border)] rounded-lg p-4">
            {backImage ? (
              <div className="space-y-2">
                <img
                  src={backImage.startsWith('http') ? backImage : `${API_BASE_URL.replace('/api', '')}${backImage}`}
                  alt="Back"
                  className="w-full h-48 object-contain rounded"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect('back', e)}
                  disabled={disabled || uploading.back}
                  className="hidden"
                  id={`${type}-back-upload`}
                />
                <label htmlFor={`${type}-back-upload`}>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={disabled || uploading.back}
                    isLoading={uploading.back}
                    className="w-full"
                  >
                    {uploading.back ? 'Uploading...' : 'Replace Back Image'}
                  </Button>
                </label>
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect('back', e)}
                  disabled={disabled || uploading.back}
                  className="hidden"
                  id={`${type}-back-upload`}
                />
                <label htmlFor={`${type}-back-upload`}>
                  <div className="flex flex-col items-center justify-center py-8 cursor-pointer hover:bg-[var(--color-neutral-50)] rounded transition-colors">
                    <svg className="w-12 h-12 text-[var(--color-neutral-400)] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-[var(--color-subtext-color)]">Click to upload back image</p>
                    <p className="text-xs text-[var(--color-subtext-color)] mt-1">Max 5MB</p>
                  </div>
                </label>
              </div>
            )}
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUpload;

